import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2, Zap, Sun, BatteryCharging, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const eur0 = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const eur2 = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const kw = (v) => (v == null ? "—" : `${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 1 })} kWh`);

function Fasce({ fasce }) {
  const carica = (fasce || []).filter((f) => f.tipo === "carica");
  const scarica = (fasce || []).filter((f) => f.tipo === "scarica");
  const Row = ({ icon: Ic, color, label, items }) => (
    <div className="flex items-start gap-2" data-testid={`accumulo-fascia-${label}`}>
      <Ic className="mt-0.5 h-4 w-4 shrink-0" style={{ color }} />
      <div className="flex flex-wrap gap-1.5">
        {items.length ? items.map((f, i) => (
          <span key={i} className="rounded-full px-2 py-0.5 text-xs font-bold" style={{ background: `${color}1A`, color }}>{f.da}–{f.a}</span>
        )) : <span className="text-xs text-slate-400">—</span>}
      </div>
    </div>
  );
  return (
    <div className="mt-3 space-y-2 rounded-2xl bg-slate-50 p-3">
      <Row icon={ArrowDownToLine} color="#16A34A" label="carica" items={carica} />
      <Row icon={ArrowUpFromLine} color="#D97706" label="scarica" items={scarica} />
    </div>
  );
}

function PianoModo({ p }) {
  if (!p || p.disponibile === false) return <p className="py-6 text-center text-sm text-slate-400" data-testid="accumulo-modo-vuoto">{p?.nota || "Non disponibile per oggi."}</p>;
  if (!p.attuabile) return (
    <div className="py-4 text-center">
      <p className="text-sm text-slate-500" data-testid="accumulo-modo-nonattuabile">{p.nota}</p>
    </div>
  );
  return (
    <div data-testid="accumulo-modo-piano">
      <div className="mt-2 rounded-2xl bg-gradient-to-br from-emerald-50 to-lime-50 p-4 text-center">
        <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Risparmio stimato</div>
        <div className="text-3xl font-black text-emerald-700" data-testid="accumulo-risparmio-anno">{eur0(p.risparmio_eur_anno)}<span className="text-base font-bold">/anno</span></div>
        <div className="text-xs text-slate-500">circa {eur2(p.risparmio_eur_giorno)} al giorno · energia spostata {kw(p.energia_kwh)}</div>
      </div>
      {p.prezzo_economico_eur_kwh != null && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-center">
          <div className="rounded-xl bg-green-50 p-2"><b className="block text-sm text-green-700">{Number(p.prezzo_economico_eur_kwh).toFixed(3)} €/kWh</b><span className="text-[11px] text-slate-500">ore economiche</span></div>
          <div className="rounded-xl bg-amber-50 p-2"><b className="block text-sm text-amber-700">{Number(p.prezzo_caro_eur_kwh).toFixed(3)} €/kWh</b><span className="text-[11px] text-slate-500">ore care</span></div>
        </div>
      )}
      <Fasce fasce={p.fasce} />
      <p className="mt-2 text-xs text-slate-500">{p.nota}</p>
    </div>
  );
}

// Fase 1 "piano consigliato": mostra quando conviene caricare/scaricare l'accumulo (non comanda la batteria)
export function ClienteAccumulo({ open, onClose, src }) {
  const [d, setD] = useState(null);
  const [modo, setModo] = useState("autoconsumo");
  useEffect(() => {
    if (!open) return;
    setD(null);
    setModo("autoconsumo");
    src.accumuloPiano(format(new Date(), "yyyy-MM-dd")).then(setD).catch(() => setD({ disponibile: false, motivo: "errore" }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const nonDisp = d && d.disponibile === false;
  const msg = { pro: "Il time-shifting intelligente è una funzione della Versione Pro.", consumi: "Serve la lettura dei consumi in tempo reale per calcolare il piano.", no_batteria: "Questo impianto non ha un accumulo.", errore: "Piano non disponibile al momento." }[d?.motivo] || "Non disponibile per oggi.";
  const punOk = d?.pun?.disponibile !== false;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="cliente-font max-w-md rounded-3xl bg-white text-slate-800" data-testid="cliente-accumulo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-extrabold"><Zap className="h-5 w-5 text-amber-500" /> Accumulo intelligente</DialogTitle>
          <DialogDescription className="text-xs font-semibold text-slate-400">Piano consigliato di oggi per spostare l'energia nel tempo</DialogDescription>
        </DialogHeader>
        {!d ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : nonDisp ? (
          <p className="py-10 text-center text-sm text-slate-500" data-testid="accumulo-non-disponibile">{msg}</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setModo("autoconsumo")} data-testid="accumulo-tab-autoconsumo"
                className={`flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm font-bold transition-colors ${modo === "autoconsumo" ? "bg-lime-500 text-white" : "bg-slate-100 text-slate-600"}`}>
                <Sun className="h-4 w-4" /> Autoconsumo
              </button>
              <button type="button" onClick={() => punOk && setModo("pun")} disabled={!punOk} data-testid="accumulo-tab-pun"
                className={`flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm font-bold transition-colors ${modo === "pun" ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600"} ${!punOk ? "opacity-40" : ""}`}>
                <BatteryCharging className="h-4 w-4" /> Prezzi PUN
              </button>
            </div>
            <PianoModo p={modo === "pun" ? d.pun : d.autoconsumo} />
            <p className="mt-3 rounded-xl bg-amber-50 p-2 text-center text-[11px] text-amber-700" data-testid="accumulo-disclaimer">
              Piano consigliato: imposta queste fasce sul tuo inverter. La funzione non comanda da sola la batteria.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
